import { Table, Tag, Typography, Button, Space, Tooltip, Modal } from "antd";
import { EyeOutlined, DownloadOutlined } from "@ant-design/icons";
import { APPLICATION_STATUS } from "../../types/jobapplication";

const { Text, Link } = Typography;

export default function ProfileFormsTable({ applications = [], loading = false }) {
  const columns = [
    {
      title: "Position Applied For",
      dataIndex: "position",
      key: "position",
      render: (position) => <Text strong>{position}</Text>,
    },
    {
      title: "Submission Date",
      dataIndex: "appliedAt",
      key: "appliedAt",
      render: (timestamp) => {
        if (!timestamp) return 'Unknown';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      },
      sorter: (a, b) => {
        const dateA = a.appliedAt?.toDate ? a.appliedAt.toDate() : new Date(a.appliedAt);
        const dateB = b.appliedAt?.toDate ? b.appliedAt.toDate() : new Date(b.appliedAt);
        return dateA - dateB;
      },
      defaultSortOrder: 'descend',
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = 'default';
        let displayText = status;
        
        switch (status) {
          case APPLICATION_STATUS.PENDING:
            color = 'orange';
            displayText = 'Under Review';
            break;
          case APPLICATION_STATUS.REVIEWED:
            color = 'blue';
            displayText = 'Being Reviewed';
            break;
          case APPLICATION_STATUS.ACCEPTED:
            color = 'green';
            displayText = 'Accepted';
            break;
          case APPLICATION_STATUS.REJECTED:
            color = 'red';
            displayText = 'Not Selected';
            break;
          default:
            color = 'default';
            displayText = status || 'Unknown';
        }

        return <Tag color={color}>{displayText.toUpperCase()}</Tag>;
      },
      filters: [
        { text: 'Under Review', value: APPLICATION_STATUS.PENDING },
        { text: 'Being Reviewed', value: APPLICATION_STATUS.REVIEWED },
        { text: 'Accepted', value: APPLICATION_STATUS.ACCEPTED },
        { text: 'Not Selected', value: APPLICATION_STATUS.REJECTED },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Resume",
      dataIndex: "resumeFileName",
      key: "resumeFileName",
      render: (fileName, record) => (
        <Space>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {fileName || 'resume.pdf'}
          </Text>
          {record.resumeUrl && (
            <Tooltip title="Download Resume">
              <Button 
                type="link" 
                icon={<DownloadOutlined />} 
                size="small"
                href={record.resumeUrl}
                target="_blank"
                download
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Application Details">
            <Button 
              type="link" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => handleViewApplication(record)}
            />
          </Tooltip>
          {record.resumeUrl && (
            <Tooltip title="Download Resume">
              <Button 
                type="link" 
                icon={<DownloadOutlined />} 
                size="small"
                href={record.resumeUrl}
                target="_blank"
                download={record.resumeFileName || 'resume.pdf'}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const handleViewApplication = (application) => {
    // You can implement a modal to show application details
    console.log('View application:', application);
    
    // Example: Show application details in a modal
    // You can implement this based on your UI framework
    Modal.info({
      title: `Application for ${application.position}`,
      content: (
        <div>
          <p><strong>Position:</strong> {application.position}</p>
          <p><strong>Applied on:</strong> {application.appliedAt?.toDate?.().toLocaleDateString()}</p>
          <p><strong>Status:</strong> {application.status}</p>
          <p><strong>Technical Expertise:</strong></p>
          <p>{application.expertise}</p>
          {application.notes && (
            <p><strong>HR Notes:</strong> {application.notes}</p>
          )}
        </div>
      ),
      width: 600,
    });
  };

  // Transform data for the table
  const tableData = applications.map(app => ({
    key: app.id,
    id: app.id,
    position: app.position,
    appliedAt: app.appliedAt,
    status: app.status,
    resumeFileName: app.resumeFileName,
    resumeUrl: app.resumeUrl,
    expertise: app.expertise,
    notes: app.notes,
    firstName: app.firstName,
    lastName: app.lastName,
  }));

  return (
    <Table 
      columns={columns} 
      dataSource={tableData} 
      pagination={{ 
        pageSize: 5,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => 
          `${range[0]}-${range[1]} of ${total} applications`,
      }}
      loading={loading}
      locale={{
        emptyText: 'No job applications submitted yet.'
      }}
      scroll={{ x: 800 }}
    />
  );
}